import { HistoryActivity } from "@client/content/controls/OrderHistory/component";
import { type HistoryItem } from "@client/content/controls/OrderHistory/types";
import { getRootContainerProps, renderTrackingCodes } from "@client/content/lib/helper";
import { ModuleButton, ModuleSecondaryButton } from "@client/content/modules/Button";
import CartItems from "@client/content/modules/Cart/Items/component";
import OrderSubscriptions from "@client/content/modules/Checkout/Content/OrderSummary/Subscriptions";
import OrderTotals from "@client/content/modules/Checkout/Content/OrderSummary/Totals";
import { type Checkout } from "@client/content/modules/Checkout/types";
import { type RootContainerProps } from "@client/content/types";
import { I18nLabelProvider } from "@client/context/i18n";
import date from "@client/lib/date";
import { joinDefined } from "@client/lib/misc";
import { IconType, type WithIsEditMode } from "@client/types";
import { getTooltipAttributes } from "@common/contexts/Tooltip";
import { type Nullable } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { toCamelCase } from "@common/lib/misc";
import classNames from "classnames";
import * as React from "react";
import { type OrderConfirmation } from "../types";
import AddCommentModal from "./AddCommentModal/container";

type OrderItemLineProps = {
  className?: string;
  name: string;
  children: any;
  qa?: string;
};
export const OrderItemLine = (props: OrderItemLineProps) =>
  !!props.children ? (
    <div className={classNames("item", props.className)}>
      <span className="name">{props.name}:</span>
      <span className="value" data-name={props.qa}>
        {props.children}
      </span>
    </div>
  ) : null;

export type MapProps = RootContainerProps &
  WithIsEditMode & {
    isDownloading: boolean;
    noImageUrl: string;
    history?: HistoryItem[];
    orderSummary: OrderConfirmation.OrderSummary;
    isAddCommentButtonHidden?: boolean;
  };
export interface ActionProps {
  onDownload: () => void;
  onClickAddComment?: () => void;
}
export type Props = ActionProps & MapProps;

const BREAK = {};
const renderAddress = (address: Partial<Nullable<Checkout.View.UserAddress>>) => {
  const parts = [
    address.unit,
    address.street,
    address.unit || address.street ? BREAK : null,
    address.city,
    address.subdivisionName,
    address.city || address.subdivisionName ? BREAK : null,
    address.countryName,
    address.postalCode
  ].filter((x) => !!x);

  return (
    <>
      {parts.map((x, i) =>
        x === BREAK ? (
          <br key={i} />
        ) : (
          <span key={i}>
            {x as string}
            {i < parts.length - 1 ? <>,&nbsp;</> : null}
          </span>
        )
      )}
    </>
  );
};

export const OrderBillingDetails = (props: {
  billingReferences: OrderConfirmation.BillingReference[];
  order: OrderConfirmation.Order;
}) => {
  const i18n = useI18n();

  return (
    <div className="billing_details">
      <h4>{i18n.billingDetails}</h4>
      {props.billingReferences.map((x) => (
        <OrderItemLine key={x.name} name={x.name}>
          {x.value}
        </OrderItemLine>
      ))}
      <OrderItemLine name={i18n.name}>
        {joinDefined(" ", props.order.billingFirstName, props.order.billingLastName)}
      </OrderItemLine>
      <OrderItemLine name={i18n.email}>{props.order.billingEmail}</OrderItemLine>
      <OrderItemLine name={i18n.phone}>{props.order.billingTelephone}</OrderItemLine>
      <OrderItemLine name={i18n.address}>
        {renderAddress({
          city: props.order.billingCity,
          countryCode: props.order.billingCountryCode,
          subdivisionCode: props.order.billingSubdivisionCode,
          postalCode: props.order.billingPostalCode,
          street: props.order.billingStreet,
          unit: props.order.billingUnit,
          countryName: props.order.billingCountryName,
          subdivisionName: props.order.billingSubdivisionName
        })}
      </OrderItemLine>
    </div>
  );
};

type OrderShippingDetailsProps = {
  order: OrderConfirmation.Order;
  orderItems: OrderConfirmation.OrderItem[];
};
export const OrderShippingDetails = (props: OrderShippingDetailsProps) => {
  const i18n = useI18n();

  if (props.order.minOrderShippingStatusKey === "NotRequired") return null;

  return (
    <div className="shipping_details">
      <h4>{i18n.shippingDetails}</h4>
      <OrderItemLine name={i18n.trackingCode}>{renderTrackingCodes(props.orderItems)}</OrderItemLine>
      <OrderItemLine name={i18n.pickupCode}>{props.order.shippingPickupPointCode}</OrderItemLine>
      <OrderItemLine name={i18n.name}>
        {joinDefined(" ", props.order.shippingFirstName, props.order.shippingLastName)}
      </OrderItemLine>
      <OrderItemLine name={i18n.email}>{props.order.shippingEmail}</OrderItemLine>
      <OrderItemLine name={i18n.phone}>{props.order.shippingTelephone}</OrderItemLine>
      <OrderItemLine name={i18n.address}>
        {renderAddress({
          city: props.order.shippingCity,
          countryCode: props.order.shippingCountryCode,
          subdivisionCode: props.order.shippingSubdivisionCode,
          postalCode: props.order.shippingPostalCode,
          street: props.order.shippingStreet,
          unit: props.order.shippingUnit,
          countryName: props.order.shippingCountryName,
          subdivisionName: props.order.shippingSubdivisionName
        })}
      </OrderItemLine>
    </div>
  );
};

type OrderSummaryProps = {
  orderSummary: OrderConfirmation.OrderSummary;
};
export const OrderInfo = ({ orderSummary }: OrderSummaryProps) => {
  const i18n = useI18n();

  return (
    <div className="info">
      <div className="summary">
        <h4>{i18n.orderSummary}</h4>
        <OrderItemLine name={i18n.order}>{orderSummary.order.number}</OrderItemLine>
        <OrderItemLine name={i18n.date}>{date.formatDateTime(orderSummary.order.orderedOnDate)}</OrderItemLine>
        <OrderItemLine name={i18n.paymentStatus}>{orderSummary.order.orderPaymentStatusName}</OrderItemLine>
        <OrderItemLine name={i18n.orderStatus}>{orderSummary.order.minOrderStatusName}</OrderItemLine>
        <OrderItemLine name={i18n.shippingStatus}>{orderSummary.order.minOrderShippingStatusName}</OrderItemLine>
        <OrderItemLine name={i18n.event}>{orderSummary.order.eventName}</OrderItemLine>
        <OrderItemLine name={i18n.stand}>{orderSummary.order.eventDateLocationName}</OrderItemLine>
        {!!orderSummary.order.eventOpenDate && (
          <OrderItemLine name={i18n.openDate}>{date.formatDate(orderSummary.order.eventOpenDate)}</OrderItemLine>
        )}

        {orderSummary.attributes.map((x, i) => (
          <OrderItemLine key={i} name={x.label}>
            {x.value}
          </OrderItemLine>
        ))}
      </div>
      <OrderBillingDetails billingReferences={orderSummary.billingReferences} order={orderSummary.order} />

      <OrderShippingDetails order={orderSummary.order} orderItems={orderSummary.orderItems} />
    </div>
  );
};

type OrderInfoProps = {
  isEditMode: boolean;
  noImageUrl: string;
  orderSummary: OrderConfirmation.OrderSummary;
  actions?: any;
};
export const OrderSummary = ({ orderSummary, ...props }: OrderInfoProps) => {
  const i18n = useI18n();

  return (
    <div className="wsc_order_summary">
      <OrderInfo orderSummary={orderSummary} />

      <div className="wsc_cart_items">
        <h4>{i18n.shoppingCart}</h4>
        <CartItems
          redirectOnClick={!props.isEditMode}
          imageSize={86}
          currencyCode={orderSummary.order.currencyCode}
          items={orderSummary.orderItems}
          noImageUrl={props.noImageUrl}
          shouldDisplayTax={false}
        />
      </div>

      <div className="bottom_wrap">
        <div className="activity_wrap">
          {!!orderSummary.history.length && (
            <>
              <h4 className="title">{i18n.activity}</h4>

              <I18nLabelProvider category="OrderHistory">
                <HistoryActivity
                  labelKeyMapper={(x) => toCamelCase(x.replace("historyType_Order", "").replaceAll("_", ""))}
                  history={orderSummary.history}
                  isNoTitle
                />
              </I18nLabelProvider>
            </>
          )}
        </div>
        <div className="payment_details">
          <h4 className="title">{i18n.paymentDetails}</h4>
          <OrderTotals
            doesRequireShipping={orderSummary.orderItems.some((x) => x.requiresShipping)}
            isConfirmation
            isEstimatedTax={false}
            currencyCode={orderSummary.order.currencyCode}
            totals={orderSummary.totals}
          />
          <OrderSubscriptions currencyCode={orderSummary.order.currencyCode} items={orderSummary.orderItems} />
          {props.actions}
        </div>
      </div>
    </div>
  );
};

export default ({ orderSummary, ...props }: Props) => {
  const i18n = useI18n();

  return (
    <div className={classNames("wsc_content_order_confirmation")}>
      <OrderSummary
        isEditMode={props.isEditMode}
        noImageUrl={props.noImageUrl}
        orderSummary={orderSummary}
        actions={
          <>
            <div className="buttons">
              {props.onClickAddComment && (
                <div
                  {...getTooltipAttributes(
                    orderSummary.order.minOrderStatusKey === "Fulfilled" ? i18n.infoAddCommentDisabled : ""
                  )}
                >
                  <ModuleSecondaryButton
                    {...getRootContainerProps(props)}
                    onClick={props.onClickAddComment}
                    icon={IconType.Comments}
                    caption={i18n.addComment}
                    isDisabled={orderSummary.order.minOrderStatusKey === "Fulfilled"}
                  />
                </div>
              )}

              <ModuleButton
                {...getRootContainerProps(props)}
                isDisabled={props.isDownloading}
                onClick={props.onDownload}
                caption={i18n.downloadInvoice}
              />
            </div>
          </>
        }
      />

      <AddCommentModal {...getRootContainerProps(props)} isModal={false} />
    </div>
  );
};
